import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { DashboardDataSource, WidgetOf } from '@/api/types'
import { useDataSourceResult } from '../queries'
import { summaryText } from '../result'
import { WidgetFrame } from './widget-frame'

/**
 * One number over a whole table.
 *
 * The data source says which aggregation and over which field; the dispatch says
 * what it came to. Both are needed to write it down, which is why the widget
 * says it has nothing rather than showing a zero when either is missing -- an
 * aggregation nobody has finished configuring has no answer, and `0` is an
 * answer.
 */
export function SummaryWidget({
  widget,
  dashboardId,
  source,
  actions,
}: {
  widget: WidgetOf<'summary'>
  dashboardId: number
  source: DashboardDataSource
  actions?: ReactNode
}) {
  const { t } = useTranslation()
  const result = useDataSourceResult(dashboardId, source.id)

  const value =
    source.type === 'local_baserow_aggregate_rows' && result.data !== undefined
      ? summaryText(source, result.data)
      : ''

  return (
    <WidgetFrame
      widgetId={widget.id}
      title={widget.title}
      description={widget.description}
      misconfigured={result.isError}
      loading={result.isPending}
      actions={actions}
    >
      {value === '' ? (
        <p className="text-muted-foreground">{t('widget.noValue')}</p>
      ) : (
        <p className="truncate text-3xl font-medium tabular-nums">{value}</p>
      )}
    </WidgetFrame>
  )
}
